import { api } from './client';

export interface AdminOrder {
  id: string;
  tenantId: string;
  tenantName: string;
  subscriptionId: string;
  systemName: string;
  planName: string;
  type: 'NEW' | 'RENEW' | 'UPGRADE' | 'DOWNGRADE';
  amount: number;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  createdAt: string;
  updatedAt: string;
}

export interface AdminTenant {
  id: string;
  name: string;
  status: 'TRIAL' | 'ACTIVE' | 'SUSPENDED';
  userCount: number;
  subscriptionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminTenantUser {
  id: string;
  email: string;
  name: string;
  status: string;
  createdAt: string;
}

export interface AdminTenantSubscription {
  id: string;
  systemId: string;
  systemName: string;
  planId: string;
  planName: string;
  status: string;
  startAt: string;
  endAt: string;
}

export interface AdminTenantDetail extends AdminTenant {
  users: AdminTenantUser[];
  subscriptions: AdminTenantSubscription[];
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface OrdersResponse {
  data: AdminOrder[];
  pagination: Pagination;
}

export interface TenantsResponse {
  data: AdminTenant[];
  pagination: Pagination;
}

export interface AdminStats {
  overview: {
    tenantCount: number;
    userCount: number;
    orderCount: number;
    activeSubscriptionCount: number;
    totalRevenue: number;
  };
  today: {
    orderCount: number;
    revenue: number;
  };
}

export interface PlatformUserRow {
  id: string;
  email: string;
  name: string;
  status: string;
  isProtected: boolean;
  createdAt: string;
  updatedAt: string;
  roles?: Array<{ role: { id: string; name: string; scope?: string } }>;
}

export interface PermissionRow {
  id: string;
  code: string;
  scope: string;
  /** API | MENU | BUTTON，与 Phase 16 元数据一致 */
  resourceKind: 'API' | 'MENU' | 'BUTTON';
  name: string | null;
  description: string | null;
}

export interface MenuCatalogRow {
  path: string;
  label: string;
  permissions: string[];
  group?: string;
  notes?: string;
}

export interface PlatformAdminApiRow {
  method: string;
  path: string;
  permissions: string[];
  note?: string;
}

export interface PermissionCatalogResponse {
  permissions: PermissionRow[];
  platformMenus: MenuCatalogRow[];
  tenantMenus: MenuCatalogRow[];
  platformAdminApis: PlatformAdminApiRow[];
}

export interface AuditLogRow {
  id: string;
  createdAt: string;
  actorUserId: string;
  actorEmail: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  metadata: unknown;
  /** 租户审计有值；平台审计为 null */
  tenantId?: string | null;
}

export interface AuditLogsResponse {
  data: AuditLogRow[];
  pagination: Pagination;
}

/**
 * 获取所有订单（管理员）
 */
export async function getAllOrders(
  page = 1,
  limit = 20,
  tenantId?: string,
  status?: string
): Promise<OrdersResponse> {
  const response = await api.get('/api/admin/orders', {
    params: { page, limit, tenantId, status },
  });
  return response.data;
}

/**
 * 获取所有租户（管理员）
 */
export async function getAllTenants(
  page = 1,
  limit = 20
): Promise<TenantsResponse> {
  const response = await api.get('/api/admin/tenants', {
    params: { page, limit },
  });
  return response.data;
}

/**
 * 获取管理员统计数据
 */
export async function getAdminStats(): Promise<AdminStats> {
  const response = await api.get('/api/admin/stats');
  return response.data;
}

/**
 * 获取租户详情
 */
export async function getTenantDetail(tenantId: string): Promise<AdminTenantDetail> {
  const response = await api.get(`/api/admin/tenants/${tenantId}`);
  return response.data;
}

/**
 * 更新租户信息
 */
export async function updateTenant(
  tenantId: string,
  data: { name?: string; status?: string }
): Promise<AdminTenant> {
  const response = await api.put(`/api/admin/tenants/${tenantId}`, data);
  return response.data;
}

export async function getPlatformUsers(): Promise<PlatformUserRow[]> {
  const response = await api.get('/api/admin/platform-users');
  return response.data;
}

export async function createPlatformUser(data: {
  email: string;
  password: string;
  name?: string;
  roleId?: string;
}): Promise<PlatformUserRow> {
  const response = await api.post('/api/admin/platform-users', data);
  return response.data;
}

export async function setPlatformUserRole(
  userId: string,
  roleId: string,
): Promise<PlatformUserRow> {
  const response = await api.patch(`/api/admin/platform-users/${userId}/role`, {
    roleId,
  });
  return response.data;
}

export async function updatePlatformUser(
  id: string,
  data: { name?: string; status?: string }
): Promise<PlatformUserRow> {
  const response = await api.patch(`/api/admin/platform-users/${id}`, data);
  return response.data;
}

export async function deletePlatformUser(id: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/admin/platform-users/${id}`);
  return response.data;
}

export async function getPermissionCatalog(): Promise<PermissionCatalogResponse> {
  const response = await api.get('/api/admin/permission-catalog');
  return response.data;
}

export async function getAuditLogs(params?: {
  page?: number;
  limit?: number;
  action?: string;
}): Promise<AuditLogsResponse> {
  const response = await api.get('/api/admin/audit-logs', { params });
  return response.data;
}

export const adminApi = {
  getAllOrders,
  getAllTenants,
  getAdminStats,
  getTenantDetail,
  updateTenant,
  getPlatformUsers,
  createPlatformUser,
  setPlatformUserRole,
  updatePlatformUser,
  deletePlatformUser,
  getPermissionCatalog,
  getAuditLogs,
};
